// Dividend income math shared by the backtest metrics, Optimizer, Snapshots and Income Target Planner.
// solveIncomeTarget mirrors backend/backtest.py::solve_income_target — keep both in sync.

// Annual trailing-12-month income + current yield on market value (falls back for older cached results).
export function incomeBasis(summary) {
  if (!summary) return { annual: 0, monthly: 0, currentYield: 0 };
  const invested = Number(summary.total_invested || summary.initial_investment || 0);
  const annual = summary.annual_income_ttm != null
    ? Number(summary.annual_income_ttm)
    : (Number(summary.yield_on_cost_pct || 0) / 100) * invested;
  const fv = Number(summary.final_value || 0);
  const currentYield = summary.current_yield_pct != null
    ? Number(summary.current_yield_pct)
    : (fv > 0 ? (annual / fv) * 100 : 0);
  return { annual, monthly: annual / 12, currentYield };
}

// Each purchase earns today's current yield; existing income grows at the dividend-growth rate.
// Yield on market value stays constant, so value = income / yield.
export function solveIncomeTarget({ monthlyTarget, years, growthPct, currentYieldPct, drip }) {
  const y = Number(currentYieldPct) / 100;
  const n = Math.round(Number(years) * 12);
  const T = Number(monthlyTarget) * 12;
  if (!(y > 0) || !(n > 0) || !(T > 0)) return null;
  const gm = (1 + Number(growthPct || 0) / 100) ** (1 / 12) - 1;
  const simulate = (lump, monthly) => {
    let income = lump * y;
    const out = [income];
    for (let m = 1; m <= n; m++) {
      income *= 1 + gm;
      income += (monthly + (drip ? income / 12 : 0)) * y;
      out.push(income);
    }
    return out;
  };
  const A = simulate(1, 0);
  const B = simulate(0, 1);
  const M = T / B[n];
  const months = [...new Set([...Array(Math.floor(n / 12) + 1).keys()].map((k) => k * 12).concat(n))].sort((a, b) => a - b);
  const projection = months.map((k) => ({
    month: k,
    year: Math.round((k / 12) * 100) / 100,
    invested: M * k,
    portfolio_value: (M * B[k]) / y,
    monthly_income: (M * B[k]) / 12,
  }));
  const hit = B.findIndex((v) => M * v >= T - 1e-6);
  return {
    lumpNow: T / y,
    lumpPatient: T / A[n],
    dca: {
      monthly: M,
      years: Number(years),
      totalContributed: M * n,
      terminalValue: (M * B[n]) / y,
      projection,
      hitMonth: hit >= 0 ? hit : null,
    },
  };
}
